import {Box, Input as JoyInput, Sheet, Stack} from "@mui/joy";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import {useShortformWasteFlavors} from "../wasteFlavor/wasteFlavor.tsx";
import {Control, SubmitHandler, useController, useForm} from "react-hook-form";
import {NewWasteFood, WasteFlavorStub, WasteStampFlavor} from "../rpc/webrpc-client.gen.ts";
import {NumericFormat} from "react-number-format";
import {FormEvent, useCallback, useEffect, useState} from "react";
import {ActionButton} from "../components/ActionButton.tsx";
import {useBundleWasteDefaults} from "../bundle/bundle.tsx";
import {useSpawnWasteMutation, useStampWasteMutation} from "../tq/tqx.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {NADA} from "../util/arutil.tsx";
import {strToFixedPoint} from "../tq/tqutil.tsx";

type WasteShortformForm = {
    [flavorId: string]: string;
}

interface WasteShortformInputProps {
    control: Control<WasteShortformForm>;
    flavor: WasteFlavorStub;
}

function WasteShortformInput({control, flavor}: WasteShortformInputProps) {
    const {t} = useTranslation();
    const fieldName = flavor.id;

    const {field} = useController<WasteShortformForm>({
        name: fieldName,
        control,
    });

    return <Sheet variant="plain" sx={{p: 1, borderRadius: 4, fontSize: "large", flex: 0}}>
        <Stack direction="row" sx={{alignItems: "center"}}>
            <NumericFormat
                decimalScale={1}
                value={field.value}
                onValueChange={(values) => {
                    const target = {
                        name: field.name,
                        value: values.value,
                    };
                    // console.log(JSON.stringify(target));
                    field.onChange({target});
                }}
                customInput={JoyInput}
                endDecorator={t("volumeUnit")}
                slotProps={{input: {type: "number"}}}
                sx={{width: "7rem"}}
                valueIsNumericString={true}
            />
        </Stack>
    </Sheet>
}

interface WasteShortformLabelProps {
    flavor: WasteFlavorStub;
}

function WasteShortformLabel({flavor}: WasteShortformLabelProps) {
    return <Sheet variant="plain" sx={{p: 1, borderRadius: 4, fontSize: "medium", flex: 1}}>
        {flavor.displayName}
    </Sheet>
}

export interface WasteShortformProps {
    bundleId: string;
    onClose: () => void;
}

export function sliceEvenly<T>(raw: T[], width: number): T[][] {
    const rv = [];
    let slice = [];
    for (const item of raw) {
        slice.push(item);
        if (slice.length === width) {
            rv.push(slice);
            slice = [];
        }
    }
    if (slice.length > 0) {
        rv.push(slice);
    }
    return rv;
}

interface WasteShortformSpawnProps {
    flavorId: string;
    volDm3Str: string;
}

export function WasteShortform(props: WasteShortformProps) {
    const {t} = useTranslation();
    const {data: flavorsData} = useShortformWasteFlavors();
    const flavors = flavorsData ?? NADA;
    const {bundleId, onClose} = props;

    const {
        control,
        handleSubmit,
        reset,
    } = useForm<WasteShortformForm>({});
    useEffect(() => {
        const formDefault = Object.fromEntries(flavors.map(flavor => {
            return [flavor.id, ""];
        }));
        reset(formDefault);
    }, [flavors, reset]);

    const [isBusy, setIsBusy] = useState<boolean>(false);
    const busyBlocker = isBusy ? {disabled: true} : {};

    const {data: wasteDefaultsData} = useBundleWasteDefaults({bundleId});

    const {mutateAsync: sendSpawnWaste} = useSpawnWasteMutation();
    const spawnWaste = useCallback(({flavorId, volDm3Str}: WasteShortformSpawnProps) => {
        const volDm3 = strToFixedPoint(volDm3Str, 1);
        const spawnFood: NewWasteFood = {
            flavorId,
            invV: volDm3,
            maniV: volDm3,
            isInvManiLinked: true,
        };
        if (wasteDefaultsData?.manifestType) {
            spawnFood.manifestTypeId = wasteDefaultsData.manifestType.id;
        }
        if (wasteDefaultsData?.packingMode) {
            spawnFood.packingModeId = wasteDefaultsData.packingMode.id;
        }
        return sendSpawnWaste({bundleId, spawnFood});
    }, [bundleId, sendSpawnWaste, wasteDefaultsData?.manifestType, wasteDefaultsData?.packingMode]);

    const {mutateAsync: stampWaste} = useStampWasteMutation();
    const stampOnloaded = useCallback(({wasteId}: { wasteId: string }) => stampWaste({
        food: {wasteId, flavor: WasteStampFlavor.enum.ONLOADED}
    }), [stampWaste]);

    const onValidSubmit: SubmitHandler<WasteShortformForm> = useCallback((formData: WasteShortformForm) => {
        setIsBusy(true);
        for (const entry of Object.entries(formData)) {
            if (entry[1] !== "") {
                void spawnWaste({flavorId: entry[0], volDm3Str: entry[1]})
                    .then(({waste}) => {
                        return stampOnloaded({wasteId: waste.id});
                    });
            }
        }
        setIsBusy(false);
    }, [spawnWaste, stampOnloaded]);

    const onSubmit = (evt: FormEvent<HTMLFormElement>) => {
        void (handleSubmit(onValidSubmit))(evt).then(() => {
            onClose();
        });
    };

    if (flavors === NADA) {
        return <SuspenseDialog message="building shortform"/>
    }

    return <Sheet variant="outlined"
                  sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                  }}>
        <form onSubmit={onSubmit}>
            <Stack direction="column">
                <Grid container>
                    {flavors.map(flavor => {
                        return <Grid size={{xs: 6, md: 4}} key={`flavor ${flavor.id}`}>
                            <Stack direction="row" sx={{alignItems: "center"}}>
                                <Box sx={{flex: 0}}><WasteShortformInput control={control} flavor={flavor}/></Box>
                                <Box sx={{flex: 1}}><WasteShortformLabel flavor={flavor}/></Box>
                            </Stack>
                        </Grid>
                    })}
                </Grid>
                <ActionButton type="submit" size="lg" sx={{px: 4, py: 2, whiteSpace: "nowrap"}} {...busyBlocker}>
                    {t("submitWasteShortform")}
                </ActionButton>
            </Stack>
        </form>
    </Sheet>
}

